/**
 * Grid Formation Layout — Arena-Aware, Slot-Based
 *
 * Each side defines explicit per-count normalized X positions (slots).
 * Y positioning uses a shared anchor + per-unit stagger.
 * Portrait screens compress X slots toward center via portraitSqueeze.
 *
 * Per-encounter overrides: pass custom slot X positions to
 * getFormationPositions() to override defaults for specific fights
 * (e.g. 1 boss centered + 2 adds flanking).
 */

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ArenaBounds {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface SlotWorldPosition {
  x: number;
  y: number;
  scale: number;
  depth: number;
}

export interface ShadowWorldPosition {
  x: number;
  y: number;
  width: number;
  height: number;
  depth: number;
}

// ---------------------------------------------------------------------------
// Tunable layout config
// ---------------------------------------------------------------------------

export interface SideConfig {
  anchorY: number;                       // 0.0–1.0, vertical anchor within arena
  stepY: number;                         // per-unit Y stagger (small, for visual depth)
  scaleMult: number;                     // tunable scale multiplier
  slots: Record<number, number[]>;       // per-count X positions (normalized 0.0–1.0)
  portraitSqueeze: number;               // 0–1: how much to compress X toward center on portrait
}

export interface LayoutConfig {
  enemy: SideConfig;
  player: SideConfig;
}

export const DEFAULT_LAYOUT_CONFIG: LayoutConfig = {
  player: {
    anchorY: 0.96,
    stepY: 0.01,
    scaleMult: 1.265,
    portraitSqueeze: 0.2,
    slots: {
      1: [0.42],
      2: [0.30, 0.54],
      3: [0.17, 0.42, 0.67],
      4: [0.10, 0.27, 0.44, 0.61],
      5: [0.06, 0.21, 0.36, 0.51, 0.66],
      6: [0.04, 0.17, 0.30, 0.43, 0.56, 0.69],
    },
  },
  enemy: {
    anchorY: 0.58,
    stepY: 0.01,
    scaleMult: 1.265,
    portraitSqueeze: 0.3,
    slots: {
      1: [0.51],
      2: [0.31, 0.54],
      3: [0.29, 0.52, 0.78],
      4: [0.22, 0.38, 0.57, 0.76],
      5: [0.21, 0.36, 0.55, 0.71, 0.84],
      6: [0.11, 0.27, 0.45, 0.59, 0.73, 0.88],
    },
  },
};

// ---------------------------------------------------------------------------
// Layout class
// ---------------------------------------------------------------------------

export class GridFormationLayout {
  private arena: ArenaBounds;
  private config: LayoutConfig;

  constructor(arena: ArenaBounds, config?: Partial<LayoutConfig>) {
    this.arena = arena;
    this.config = {
      enemy: { ...DEFAULT_LAYOUT_CONFIG.enemy, ...config?.enemy },
      player: { ...DEFAULT_LAYOUT_CONFIG.player, ...config?.player },
    };
  }

  updateArenaBounds(arena: ArenaBounds): void {
    this.arena = arena;
  }

  updateConfig(config: Partial<LayoutConfig>): void {
    if (config.enemy) this.config.enemy = { ...this.config.enemy, ...config.enemy };
    if (config.player) this.config.player = { ...this.config.player, ...config.player };
  }

  // -------------------------------------------------------------------------
  // Dynamic formation positions
  // -------------------------------------------------------------------------

  /**
   * @param side       'player' or 'enemy'
   * @param count      number of units on this side
   * @param spriteScales  per-unit scale multipliers from sprite config
   * @param slotOverrides optional X positions (0.0–1.0) to override defaults
   *                      for per-encounter customization
   */
  getFormationPositions(
    side: 'player' | 'enemy',
    count: number,
    spriteScales: number[],
    slotOverrides?: number[],
  ): SlotWorldPosition[] {
    const cfg = this.config[side];
    const maxCount = 6;
    const n = Math.max(1, Math.min(count, maxCount));

    const arenaH = this.arena.bottom - this.arena.top;
    const arenaW = this.arena.right - this.arena.left;
    const aspect = arenaW / arenaH;

    // --- X positions (slot-based) ---
    const rawSlots = slotOverrides ?? cfg.slots[n];
    // Fallback: if no slots defined for this count, generate evenly spaced
    const slotXs = rawSlots && rawSlots.length === n
      ? rawSlots
      : Array.from({ length: n }, (_, i) => 0.2 + (0.6 * i) / Math.max(1, n - 1));

    // Portrait squeeze: compress X positions toward their center on narrow screens
    let finalXs: number[];
    if (aspect < 1 && cfg.portraitSqueeze > 0) {
      const centerX = slotXs.reduce((a, b) => a + b, 0) / n;
      const squeeze = 1 - cfg.portraitSqueeze * (1 - aspect);
      finalXs = slotXs.map(x => centerX + (x - centerX) * squeeze);
    } else {
      finalXs = slotXs;
    }

    // --- Y positions (anchor + stagger, centered) ---
    const rawYs = Array.from({ length: n }, (_, i) => i * cfg.stepY);
    const comY = rawYs.reduce((a, b) => a + b, 0) / n;
    const finalYs = rawYs.map(y => cfg.anchorY + (y - comY));

    // --- Build world positions ---
    const positions: SlotWorldPosition[] = [];
    for (let i = 0; i < n; i++) {
      const x = this.arena.left + finalXs[i] * arenaW;
      const y = this.arena.top + finalYs[i] * arenaH;

      const spriteScale = spriteScales[i] ?? 1.0;
      const scale = (arenaH / 1500) * cfg.scaleMult * spriteScale;
      const depth = Math.floor(y);

      positions.push({ x, y, scale, depth });
    }

    return positions;
  }

  // -------------------------------------------------------------------------
  // Shadow
  // -------------------------------------------------------------------------

  getShadowPosition(
    unitPos: SlotWorldPosition,
    displayWidth: number,
    shadow: { scale: number; offsetX: number; offsetY: number },
  ): ShadowWorldPosition {
    const sw = displayWidth * shadow.scale;
    return {
      x: unitPos.x + displayWidth * shadow.offsetX,
      y: unitPos.y + displayWidth * shadow.offsetY,
      width: sw,
      height: sw * 0.22,
      depth: unitPos.depth - 1,
    };
  }
}
